/**
 * COMPANY CASES (/company-cases) — the scope bar's pure decisions: which
 * companies / lead agencies show, and which company + agency a visit lands on.
 * `composables/useCaseNavigation.js` owns the state, the URL and the traces and
 * calls these. Extracted in the /company-cases refactor (2026-10-05),
 * behaviour unchanged. The company-bar refactor (M22, 2026-10-12) added the
 * nav payload's normalisation, the unread merge and the URL sync below.
 */

const byDisplayOrder = (a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0)

/**
 * The case ids a nav fetch asks unread counts for: the explicit override, else
 * what the page's getter says it is showing (as strings), else null. A getter
 * that throws or returns nothing means "no slice".
 */
export function currentCaseIdsFor(overrideIds, getter) {
  let ids = overrideIds ?? null
  if (ids == null && typeof getter === 'function') {
    try {
      const got = getter()
      if (Array.isArray(got) && got.length) ids = got.map(String)
    } catch {
      ids = null
    }
  }

  return ids
}

/**
 * The `getCaseNavData` payload as the bar reads it: companies AND their nested
 * lead agencies ordered by `displayOrder` (the nested relation doesn't come
 * pre-sorted), the viewed user's company order as strings, the unread slice.
 */
export function normalizeNavPayload(payload) {
  const p = payload ?? { companies: [], cases: [], companyOrder: [] }

  return {
    companyOrder: Array.isArray(p.companyOrder) ? p.companyOrder.map(String) : [],
    companies: (Array.isArray(p.companies) ? p.companies : [])
      .map(c => ({ ...c, leadAgencies: [...(c.leadAgencies ?? [])].sort(byDisplayOrder) }))
      .sort(byDisplayOrder),
    cases: Array.isArray(p.cases) ? p.cases : [],
  }
}

/**
 * The per-case unread map after a nav fetch: the slice's counts written over
 * the previous map (a new Map); the same map back when the slice is empty.
 */
export function mergeCaseUnread(prev, cases) {
  if (!Array.isArray(cases) || !cases.length) return prev

  const next = new Map(prev)
  for (const c of cases) {
    if (c?.id != null) next.set(String(c.id), c.unreadTrackingCount ?? 0)
  }

  return next
}

/**
 * The address bar after a selection: `cid` / `aid` / `caid` set or dropped,
 * every other param kept, and whether anything changed (compared as strings,
 * so '12' and 12 are the same selection).
 */
export function nextNavQuery(current, { companyId, leadAgencyId, caseId }) {
  const cur = current ?? {}
  const next = { ...cur }

  if (companyId) next.cid = String(companyId)
  else delete next.cid

  if (leadAgencyId) next.aid = String(leadAgencyId)
  else delete next.aid

  if (caseId) next.caid = String(caseId)
  else delete next.caid

  const changed = ['cid', 'aid', 'caid'].some(k => String(cur[k] ?? '') !== String(next[k] ?? ''))

  return { next, changed }
}

/**
 * Whose company order the bar follows: the viewed user's while viewing as
 * someone (so the bar mirrors their arrangement), else the caller's own.
 */
export function effectiveCompanyOrder(viewAsUserId, viewedOrder, ownOrder) {
  return viewAsUserId != null ? viewedOrder : ownOrder
}

/**
 * The bar's companies: only those with cases, each with only its agencies
 * that have cases — plus the SELECTED company / agency, so the active pill
 * never vanishes. (The caller then applies the user's own order.)
 */
export function visibleNavCompanies(companies, selectedCompanyId, selectedLeadAgencyId) {
  if (companies.length === 0) return []

  const selectedId = String(selectedCompanyId ?? '')
  const selectedLaId = String(selectedLeadAgencyId ?? '')

  return companies
    .filter(c => (c.casesCount ?? 0) > 0 || String(c.id) === selectedId)
    .map(c => ({
      ...c,
      leadAgencies: (c.leadAgencies ?? []).filter(
        la => (la.casesQuantity ?? 0) > 0 || String(la.id) === selectedLaId,
      ),
    }))
}

/**
 * Which lead agency to select for a company. The bar only shows agencies with
 * cases, so the pick lands on one of those: the preferred id (URL `aid` /
 * remembered) while it still has cases, else the first with cases, else — a
 * company with no cases anywhere — its first agency. Null only without any.
 */
export function pickLeadAgencyId(company, preferredId) {
  const all = company?.leadAgencies ?? []
  const withCases = all.filter(la => (la.casesQuantity ?? 0) > 0)

  if (preferredId != null) {
    const hit = withCases.find(la => String(la.id) === String(preferredId))
    if (hit) return String(hit.id)
  }

  const fallback = withCases[0] ?? all[0] ?? null

  return fallback?.id != null ? String(fallback.id) : null
}

/**
 * The company a visit lands on: the URL's `cid` → the remembered one → the
 * first visible → the first at all. `via` says which (for the trace); `urlMissed`
 * when the URL named a company the list doesn't have.
 */
export function resolveNavCompany({ companies, visible, urlCompanyId, rememberedCompanyId }) {
  if (urlCompanyId) {
    const hit = companies.find(c => String(c.id) === String(urlCompanyId)) ?? null
    if (hit) return { company: hit, via: 'url', urlMissed: false }
  }
  const urlMissed = !!urlCompanyId

  if (rememberedCompanyId) {
    const hit = companies.find(c => String(c.id) === String(rememberedCompanyId)) ?? null
    if (hit) return { company: hit, via: `memory(${rememberedCompanyId})`, urlMissed }
  }

  const first = visible[0] ?? companies[0] ?? null

  return { company: first, via: first ? 'first-fallback' : null, urlMissed }
}

/**
 * The lead agency for that company: the URL's `aid` (clearing an explicit
 * "All") → an explicit "All" stays All (null) → the remembered one → the first
 * with cases. Returns the pick and whether "All" is (still) on.
 */
export function resolveNavLeadAgency({ company, urlLeadAgencyId, isAll, rememberedLeadAgencyId }) {
  if (urlLeadAgencyId != null) {
    return { leadAgencyId: company ? pickLeadAgencyId(company, String(urlLeadAgencyId)) : null, isAll: false }
  }
  if (isAll) return { leadAgencyId: null, isAll: true }
  if (company) return { leadAgencyId: pickLeadAgencyId(company, rememberedLeadAgencyId), isAll }

  return { leadAgencyId: null, isAll }
}
